import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer, scaleIn } from '@/lib/animations';

const faqs = [
  { q: "What's included in onboarding?", a: "Our onboarding includes a discovery call, data collection, AI training, workflow configuration, CRM integration, testing, and a launch review. We handle everything so you don't need a technical team." },
  { q: "What do you mean by 'automation'?", a: "Automation is how we connect your AI to the rest of your business. It means plugging your AI solution into the systems you already use, and — where the right systems aren't in place yet — building them for you: CRMs, integrations, workflows, and notifications. The goal is a business that runs smoothly end-to-end, not just an AI bolted on top." },
  { q: "Do I need a technical team?", a: "No. custum.ai is fully managed. We build, deploy, and maintain your AI agents. You just provide the business knowledge and we handle the tech, systems, and support." },
  { q: "Which CRM systems do you integrate with?", a: "We integrate with Zoho, Agile CRM, HubSpot, Salesforce, and can work with custom CRM systems via API. Data syncs automatically between your AI agents and CRM." },
  { q: "How long does setup take?", a: "Typical setup takes 1–2 weeks from kickoff to launch. This includes AI training, CRM integration, and testing. Complex deployments may take 3–4 weeks." },
  { q: "How is pricing structured?", a: "Every solution is custom-built for your business, so pricing depends on your specific needs — call volume, chat traffic, integrations, and features required. Book a free discovery call and we'll provide a tailored quote." },
];

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b" style={{ borderColor: 'var(--brand-border)' }}>
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${faq.q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-base font-medium text-brand-white group-hover:text-brand-blue-light transition-colors pr-4">
          {faq.q}
        </span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={20} className="text-brand-muted flex-shrink-0" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm text-brand-muted leading-relaxed">{faq.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CTASection() {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 bg-brand-charcoal relative">
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}>
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-brand-white tracking-tight mb-6">
            Ready to Deploy AI in <span className="text-brand-blue">Your Business?</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted mb-10 max-w-lg mx-auto">
            Start with a free discovery call. We'll assess your needs and build a custom AI solution tailored to your business.
          </motion.p>
          <motion.div variants={scaleIn}>
            <Link to="/contact/" data-testid="cta-book-call" className="inline-flex items-center gap-2 px-10 py-5 rounded-full bg-brand-blue text-white font-semibold text-lg transition-all duration-300 shadow-[0_0_30px_rgba(var(--c-purple-rgb),0.4)] hover:shadow-[0_0_50px_rgba(var(--c-purple-rgb),0.7)] hover:bg-brand-blue-light animate-pulse-glow">
              Book Your Free Call <ArrowRight size={20} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default function FAQPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div data-testid="faq-page" className="pt-24 min-h-screen bg-brand-black">
      <Helmet>
        <title>FAQ — custum.ai</title>
        <meta name="description" content="Answers to common questions about onboarding, automation, CRM integrations, setup time, and pricing." />
        <meta property="og:title" content="FAQ — custum.ai" />
        <meta property="og:description" content="Answers to common questions about onboarding, automation, CRM integrations, setup time, and pricing." />
        <link rel="canonical" href="https://custum.ai/faq/" />
      </Helmet>

      <section data-testid="faq-section" className="py-24 md:py-8 bg-brand-black">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-blue-light mb-4">
              FAQ
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-brand-white tracking-tight">
              Frequently Asked Questions
            </motion.h1>
          </motion.div>
          <div>
            {faqs.map((faq, i) => (
              <FAQItem
                key={i}
                faq={faq}
                isOpen={openFaq === i}
                onToggle={() => {
                  const opening = openFaq !== i;
                  setOpenFaq(opening ? i : null);
                  if (opening && window.gtag) {
                    window.gtag('event', 'faq_open', { question: faq.q });
                  }
                }}
              />
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
